import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import {
  Loader2, Package, Truck, CheckCircle2, Clock, ExternalLink,
  ShoppingBag, ChevronRight, MapPin, Mail, AlertCircle, Sparkles,
  Share2, Copy, Check,
} from "lucide-react";

const ICONS = {
  paid: ShoppingBag,
  preparing: Package,
  forwarded: Sparkles,
  shipped: Truck,
  delivered: CheckCircle2,
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const OrderTrack = () => {
  const { ref } = useParams();
  const [searchParams] = useSearchParams();
  const shareToken = searchParams.get("t");
  const { formatPrice } = useCurrency();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareStats, setShareStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = shareToken ? { t: shareToken } : {};
    api.get(`/orders/${ref}/track`, { params })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Could not load this order"))
      .finally(() => setLoading(false));
  }, [ref, shareToken]);

  // Fetch the social-proof counter once on mount — non-blocking.
  useEffect(() => {
    api.get("/track-share-stats").then((r) => setShareStats(r.data?.shares_last_7d ?? null)).catch(() => {});
  }, []);

  const logShare = (channel) => {
    api.post(`/orders/${ref}/track-share`, { channel }).catch(() => {});
  };

  const buildShareMessage = () => {
    if (!data) return "";
    const title = data.listing?.title || "an item";
    const url = data.share_url || `${window.location.origin}/orders/${ref}/track`;
    return `Look 🎉 I just bought ${title} on AfriMarket — track the delivery here: ${url}`;
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(buildShareMessage());
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
    logShare("whatsapp");
  };

  const shareNative = async () => {
    if (!navigator.share) {
      copyLink();
      return;
    }
    try {
      await navigator.share({
        title: data?.listing?.title || "My AfriMarket order",
        text: buildShareMessage(),
        url: data?.share_url || window.location.href,
      });
      logShare("native_share");
    } catch (_) { /* user cancelled */ }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(data?.share_url || window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      logShare("copy_link");
    } catch (_) { /* clipboard blocked */ }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#FAF8F5]">
        <Loader2 className="w-6 h-6 animate-spin text-[#C05A3C]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-[60vh] bg-[#FAF8F5] flex items-center justify-center px-4">
        <div className="text-center max-w-md" data-testid="track-error">
          <AlertCircle className="w-10 h-10 mx-auto text-[#C05A3C] mb-3" />
          <h1 className="font-display text-2xl mb-2">We couldn&apos;t load this order</h1>
          <p className="text-muted-foreground text-sm">{error || "Unknown error"}</p>
          <Link to="/purchases" className="inline-block mt-5 text-[#C05A3C] underline">Back to your purchases</Link>
        </div>
      </div>
    );
  }

  const { listing, steps = [] } = data;
  const heroImg = listing?.images?.[0];
  const priceLabel = formatPrice(data.amount, data.currency) || `${data.currency || ""} ${data.amount}`;
  const currentStep = steps.find((s) => s.state === "current");
  const isDelivered = steps[steps.length - 1]?.state === "complete";

  return (
    <div className="min-h-[80vh] bg-[#FAF8F5]" data-testid="order-track-page">
      <SEO
        title={`Track your order · ${listing?.title || "AfriMarket"}`}
        description="Live tracking timeline for your AfriMarket order — from payment to delivery."
        canonical={`/orders/${ref}/track`}
      />
      <div className="container-narrow py-10">
        {/* Breadcrumb */}
        <nav className="text-xs text-muted-foreground mb-5 flex items-center gap-1.5">
          <Link to="/purchases" className="hover:text-[#C05A3C]">Your purchases</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#1A2024]">Tracking</span>
        </nav>

        {/* Header card */}
        <div className="bg-white border border-black/10 rounded-2xl p-5 sm:p-6 shadow-sm mb-6" data-testid="track-header">
          <div className="flex flex-col sm:flex-row gap-5">
            {heroImg && (
              <div className="w-full sm:w-32 sm:h-32 aspect-square sm:aspect-auto rounded-lg overflow-hidden bg-[#F3EFE9] shrink-0">
                <img src={heroImg} alt={listing.title} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-[#C05A3C] font-bold">
                {isDelivered ? "Order delivered" : (currentStep?.label || "In progress")}
              </div>
              <h1 className="font-display text-2xl sm:text-3xl mt-1 leading-tight">
                {listing?.title || "Your order"}
              </h1>
              <div className="text-sm text-muted-foreground mt-1.5 flex flex-wrap items-center gap-3">
                {listing?.owner_name && <span>Sold by <strong className="text-[#1A2024]">{listing.owner_name}</strong></span>}
                {listing?.country && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{listing.country}</span>}
              </div>
              <div className="font-display text-2xl text-[#C05A3C] mt-2">{priceLabel}</div>
              <div className="text-[11px] text-muted-foreground mt-1.5 font-mono">Ref: {data.transaction_id || ref}</div>
            </div>
          </div>
        </div>

        {/* Share with family — viral diaspora chain mechanic */}
        {data.share_url && (
          <div className="bg-gradient-to-br from-[#FFF7F2] via-white to-[#F3EFE9] border border-[#C05A3C]/20 rounded-2xl p-5 sm:p-6 mb-6" data-testid="share-card">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
              <div>
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#C05A3C] font-bold mb-1">
                  <Share2 className="w-3 h-3" /> Share with family
                </div>
                <div className="font-display text-lg leading-tight">Let them follow the delivery too</div>
                <p className="text-xs text-muted-foreground mt-1 max-w-md">
                  No login needed for whoever you send this to — they just tap the link.
                </p>
                {shareStats && shareStats > 0 && (
                  <div className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-full bg-[#2A3D36]/8 text-[#2A3D36] text-[11px] font-semibold" data-testid="share-social-proof">
                    <Sparkles className="w-3 h-3" />
                    ✨ Shared {shareStats.toLocaleString()} times this week
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <Button
                  onClick={shareWhatsApp}
                  data-testid="share-whatsapp-btn"
                  className="bg-[#25D366] hover:bg-[#1FBE5B] text-white font-semibold"
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4 mr-2" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.464 3.488"/>
                  </svg>
                  WhatsApp
                </Button>
                {typeof navigator !== "undefined" && navigator.share && (
                  <Button onClick={shareNative} variant="outline" data-testid="share-native-btn">
                    <Share2 className="w-4 h-4 mr-2" /> Share
                  </Button>
                )}
                <Button onClick={copyLink} variant="outline" data-testid="share-copy-btn">
                  {copied ? <Check className="w-4 h-4 mr-2 text-[#4A5D23]" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? "Copied!" : "Copy link"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tracking number panel — only when shipped */}
        {data.tracking_number && (
          <div className="bg-gradient-to-br from-[#1A2024] to-[#2A3D36] text-white rounded-2xl p-5 sm:p-6 mb-6 shadow-md" data-testid="track-parcel-panel">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#D9923B] font-bold flex items-center gap-1.5">
                  <Truck className="w-3 h-3" /> Tracking
                </div>
                <div className="text-base sm:text-lg mt-1 font-mono">
                  {data.tracking_carrier && <span className="text-white/70 mr-2">{data.tracking_carrier} ·</span>}
                  <span className="text-white">{data.tracking_number}</span>
                </div>
              </div>
              {data.tracking_url && (
                <a
                  href={data.tracking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="track-parcel-cta"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-[#C05A3C] hover:bg-[#A8492F] rounded-lg font-semibold text-sm transition"
                >
                  <ExternalLink className="w-4 h-4" /> Track parcel
                </a>
              )}
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="bg-white border border-black/10 rounded-2xl p-5 sm:p-7 mb-6" data-testid="track-timeline">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-lg">Live timeline</h2>
            <span className="text-xs text-muted-foreground">
              Status: <strong className="text-[#1A2024]">{(data.fulfilment_status || "paid").replace("_", " ")}</strong>
            </span>
          </div>
          <ol className="relative">
            {steps.map((step, i) => {
              const Icon = ICONS[step.key] || Clock;
              const isComplete = step.state === "complete";
              const isCurrent = step.state === "current";
              const isLast = i === steps.length - 1;
              return (
                <li key={step.key} className="relative pl-12 pb-7 last:pb-0" data-testid={`track-step-${step.key}`}>
                  {/* Connector line */}
                  {!isLast && (
                    <span
                      className={`absolute left-[18px] top-9 bottom-0 w-0.5 ${isComplete ? "bg-[#4A5D23]" : "bg-black/10"}`}
                      aria-hidden="true"
                    />
                  )}
                  {/* Step dot */}
                  <span
                    className={`absolute left-0 top-0 w-9 h-9 rounded-full flex items-center justify-center ${
                      isComplete
                        ? "bg-[#4A5D23] text-white"
                        : isCurrent
                          ? "bg-[#C05A3C] text-white ring-4 ring-[#C05A3C]/15 animate-pulse"
                          : "bg-black/5 text-black/30"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </span>
                  <div className="flex items-baseline justify-between gap-4 flex-wrap">
                    <div className={`font-semibold ${isCurrent ? "text-[#C05A3C]" : isComplete ? "text-[#1A2024]" : "text-muted-foreground"}`}>
                      {step.label}
                    </div>
                    {step.at && (
                      <div className="text-xs text-muted-foreground" data-testid={`track-step-time-${step.key}`}>
                        {fmtDate(step.at)}
                      </div>
                    )}
                  </div>
                  {isCurrent && !step.at && (
                    <div className="text-xs text-muted-foreground mt-1">In progress — we&apos;ll update this as soon as the seller takes action.</div>
                  )}
                </li>
              );
            })}
          </ol>
        </div>

        {/* Help footer */}
        <div className="bg-white border border-black/10 rounded-2xl p-5 flex flex-col sm:flex-row items-start gap-3 justify-between">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-[#C05A3C]/10 text-[#C05A3C] flex items-center justify-center shrink-0">
              <Mail className="w-4 h-4" />
            </div>
            <div>
              <div className="font-semibold text-sm">Something not right?</div>
              <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
                Your AfriMarket buyer guarantee covers all cross-border orders. Open a free dispute and we&apos;ll mediate within 24h.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/complaints">
              <Button variant="outline" data-testid="track-open-dispute">Open dispute</Button>
            </Link>
            <Link to="/contact">
              <Button variant="ghost" data-testid="track-contact-us">Contact us</Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTrack;
